import Iaphub from 'react-native-iaphub';
import Product from 'react-native-iaphub/src/models/product'; // todo: need to not import from their internal models
import { getConfig } from '../config';
import { GoogleAnalytics } from '../services/google-analytics';
import { showMessage } from '../ui/components/Toast';
import { i18n, Logger } from '../utils';

interface IBuyErrorInfo {
  severity: 'info' | 'warning' | 'error';
  message: string;
}

class InAppPurchase {
  private static MappedBuyErrors: { [key: string]: IBuyErrorInfo } = {
    user_cancelled: {
      severity: 'info',
      message: 'Subscription not completed. Please try again',
    },
    product_already_owned: {
      severity: 'warning',
      message: 'Failed to subscribe. You already have a subscription',
    },
    deferred_payment: {
      severity: 'warning',
      message: 'Subscription has been deferred, awaiting parental approval',
    },
    receipt_validation_failed: {
      severity: 'error',
      message: 'Failed to subscribe. Unable to confirm payment',
    },
    receipt_request_failed: {
      severity: 'error',
      message: 'Failed to subscribe. Possible network error',
    },
    default: {
      severity: 'error',
      message: 'Failed to subscribe. Unknown error',
    },
  };

  public isInitialized: boolean = false;
  public skuProcessing: string | null = null;
  public productsForSale: Product[] = [];
  public activeProducts: Product[] = [];
  private googleAnalytics = new GoogleAnalytics();

  // Init IAPHUB
  public async init(userId: string) {
    try {
      Logger.leaveBreadcrumb('InAppPurchase.init', { userId });
      const config = getConfig();
      const environment = config.gymEnvironment;
      await Iaphub.start({
        appId: config.iapHubAppId,
        apiKey: config.iapHubApiKey,
        environment,
      });
      await this.setUserId(userId);
      this.isInitialized = true;
    } catch (err) {
      Logger.leaveBreadcrumb('InAppPurchase.init failed', {
        code: err.code,
      });
      Logger.notify(err, (report) => {
        report.errorClass = 'In app purchase initialisation failed';
      });
      // The init has failed (the error code is available in the 'err.code' property)
      // You probably forgot to specify an option (appId, apiKey...)
      // Or the user is not allowed to make payments, IOS only (Error code: 'billing_disabled')
      // Or the billing system is unavailable, it may be a problem with the device or
      // tunes/Play Store is down (Error code: 'billing_unavailable')
      // Or it is an unknown error, probably the native library of react-native-iap that is not
      // installed properly (Error code: 'billing_error')
    }
  }

  public async setUserId(userId) {
    await Iaphub.login(userId);
  }

  public async logout() {
    await Iaphub.logout();
  }

  public async getProductsForSale(): Promise<Product[]> {
    try {
      Logger.debug('InAppPurchase.getProductsForSale');
      if (!this.isInitialized) {
        throw new Error('InAppPurchase was not initialized');
      }
      this.productsForSale = await Iaphub.getProductsForSale();
      return this.productsForSale;
    } catch (err) {
      Logger.notify(err, (report: any) => {
        report.errorClass = 'Failed to fetch products for sale';
        report.errorMessage = `Products for sale error: ${err.message}`;
      });
      throw err;
    }
  }

  public async getActiveProducts(): Promise<Product[]> {
    this.activeProducts = await Iaphub.getActiveProducts();
    return this.activeProducts;
  }

  // Call this method when an user click on one of your products
  public async buy(productSku: string) {
    try {
      this.skuProcessing = productSku;
      const transaction = await Iaphub.buy(productSku);
      this.skuProcessing = null;

      if (transaction.webhookStatus === 'failed') {
        showMessage(i18n.t('auth:paymentSuccessfulWebhookFailed'));
        this.googleAnalytics.logEventWithParamaters('in_app_purchase_subscription_start_failed', {
          error: 'IAPSubscriptionSuccessfulWebhookFailed',
        });
      }

      this.googleAnalytics.logEventWithParamaters('in_app_purchase_subscription_start', {productSku});
      this.googleAnalytics.logEventWithParamaters('sign_up_start_trail_successfully', {productSku});
      return transaction;
    } catch (err) {
      this.skuProcessing = null;
      this.handleBuyError(err);
    }
  }

  private handleBuyError = (error: any): void => {
    let mappedError = InAppPurchase.MappedBuyErrors[error.code];
    if (!mappedError) {
      mappedError = InAppPurchase.MappedBuyErrors.default;
    }

    if (mappedError === InAppPurchase.MappedBuyErrors.user_cancelled) {
    this.googleAnalytics.logEventWithParamaters('in_app_purchase_subscription_cancelled');
    } else {
      this.googleAnalytics.logEventWithParamaters('in_app_purchase_subscription_failed', {
        error: mappedError.message,
      });
    }

    Logger.notify(error, (report) => {
      report.errorClass = 'In app purchase buy failed';
      report.errorMessage = mappedError.message + ', full error:' + JSON.stringify(error);
    });

    throw new Error(mappedError.message);
  }
}

export const InAppPurchaseService = new InAppPurchase();
